import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FrontHeader from '../components/common/FrontHeader';
import FrontFooter from '../components/common/FrontFooter';
import './ServiceProviders.css';

const API_BASE = (
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
).replace(/\/+$/, '');

const PAGE_SIZE = 4;

const text = value => typeof value === 'string' ? value.trim() : '';

const list = value => (
  Array.isArray(value)
    ? [...new Set(value.map(text).filter(Boolean))]
    : []
);

const count = value => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const companyName = provider =>
  text(provider.company) || text(provider.name) || 'Service provider';

const description = provider =>
  text(provider.description) || text(provider.bio);

const websiteUrl = value => {
  const input = text(value);

  if (!input) return '';

  try {
    const url = new URL(
      /^[a-z][a-z0-9+.-]*:/i.test(input)
        ? input
        : `https://${input}`
    );

    return ['http:', 'https:'].includes(url.protocol)
      ? url.href
      : '';
  } catch {
    return '';
  }
};

const resolveAvatar = provider => {
  const raw = text(provider?.avatar);

  if (
    raw &&
    /^data:image\/(?:png|jpeg|jpg|webp|gif);base64,/i.test(raw)
  ) {
    return raw;
  }

  if (raw && /^https?:\/\//i.test(raw)) {
    return raw;
  }

  if (provider?._id) {
    return `${API_BASE}/avatar/${provider._id}`;
  }

  return '';
};

const ratingText = provider => {
  const rating = Number(provider.rating);

  return count(provider.totalRatings) > 0 && Number.isFinite(rating)
    ? `${Math.min(5, Math.max(0, rating)).toFixed(1)} / 5`
    : 'No reviews yet';
};

const memberSince = value => {
  const date = new Date(value);

  return value && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric'
      })
    : 'Not provided';
};

function usePublicData(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError('');
      setData(null);

      try {
        const response = await fetch(`${API_BASE}${path}`, {
          signal: controller.signal
        });

        const result = await response.json().catch(() => null);

        if (!response.ok || !result?.success) {
          throw new Error(
            result?.message || 'Unable to load this page. Please try again.'
          );
        }

        if (!controller.signal.aborted) {
          setData(result);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => controller.abort();
  }, [path, revision]);

  return {
    data,
    loading,
    error,
    retry: () => setRevision(value => value + 1)
  };
}

function Avatar({ provider, large = false }) {
  const [failed, setFailed] = useState(false);
  const source = resolveAvatar(provider);
  const name = companyName(provider);

  useEffect(() => {
    setFailed(false);
  }, [source]);

  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();

  return (
    <div className={`spd-avatar${large ? ' spd-avatar-large' : ''}`}>
      {source && !failed ? (
        <img
          src={source}
          alt={`${name} logo`}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </div>
  );
}

function Verification({ provider }) {
  return provider.verificationStatus === 'approved' ? (
    <span className="spd-badge">
      <i className="fas fa-check-circle" aria-hidden="true" />
      Verified provider
    </span>
  ) : (
    <span className="spd-muted">Business verification pending</span>
  );
}

function ExpandableText({ value, limit = 220 }) {
  const [expanded, setExpanded] = useState(false);
  const content = text(value);

  if (!content) {
    return <p className="spd-muted">Company description not provided.</p>;
  }

  const long = content.length > limit;

  return (
    <div className="spd-description">
      <p>
        {long && !expanded ? `${content.slice(0, limit).trim()}…` : content}
      </p>
      {long && (
        <button
          type="button"
          className="spd-text-button"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

function Tags({ values, limit = 5, empty = 'Not specified' }) {
  const [expanded, setExpanded] = useState(false);
  const items = list(values);

  if (!items.length) {
    return <p className="spd-muted">{empty}</p>;
  }

  return (
    <div className="spd-tags">
      {(expanded ? items : items.slice(0, limit)).map(item => (
        <span className="spd-tag" key={item}>{item}</span>
      ))}
      {items.length > limit && (
        <button
          type="button"
          className="spd-tag spd-tag-button"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Show less' : `+${items.length - limit} more`}
        </button>
      )}
    </div>
  );
}

function MessageButton({ provider }) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const ownProfile = user &&
    String(user._id || user.id) === String(provider._id);

  if (ownProfile) {
    return (
      <Link
        className="spd-button spd-button-primary"
        to="/service-provider/company-profile"
      >
        Edit company profile
      </Link>
    );
  }

  const contact = () => {
    const target = `/messages/${provider._id}`;

    if (user) {
      navigate(target, {
        state: { userName: companyName(provider) }
      });
    } else {
      navigate('/login', {
        state: { from: target }
      });
    }
  };

  return (
    <button
      type="button"
      className="spd-button spd-button-primary"
      onClick={contact}
      disabled={loading}
    >
      <i className="far fa-envelope" aria-hidden="true" />
      {user ? 'Send message' : 'Sign in to message'}
    </button>
  );
}

function PageState({ loading, error, retry }) {
  return (
    <div className="spd-panel spd-state" role={error ? 'alert' : 'status'}>
      <i
        className={
          loading
            ? 'fas fa-spinner fa-spin'
            : 'fas fa-exclamation-circle'
        }
        aria-hidden="true"
      />
      <h2>{loading ? 'Loading providers' : 'Unable to load'}</h2>
      <p>{loading ? 'Getting the latest company information…' : error}</p>
      {!loading && (
        <button type="button" className="spd-button" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

function ProviderCard({ provider }) {
  const profileUrl = `/service-providers/${provider._id}`;

  return (
    <article className="spd-panel spd-card">
      <div className="spd-card-top">
        <Avatar provider={provider} />
        <div className="spd-identity">
          <Link className="spd-company-link" to={profileUrl}>
            <h2>{companyName(provider)}</h2>
          </Link>
          <div className="spd-meta">
            <Verification provider={provider} />
            {provider.location && (
              <span>
                <i className="fas fa-map-marker-alt" aria-hidden="true" />
                {provider.location}
              </span>
            )}
          </div>
        </div>
        <span className="spd-rating">{ratingText(provider)}</span>
      </div>

      <ExpandableText value={description(provider)} />

      <Tags
        values={provider.serviceCategories}
        limit={4}
        empty="Services not listed yet"
      />

      <div className="spd-card-bottom">
        <div className="spd-metrics">
          <span>
            <strong>{count(provider.completedJobs)}</strong> jobs completed
          </span>
          <span>
            <strong>{list(provider.ports).length}</strong> ports
          </span>
          <span>
            <strong>{list(provider.countries).length}</strong> countries
          </span>
        </div>

        <div className="spd-actions">
          <Link className="spd-button" to={profileUrl}>
            View profile <span aria-hidden="true">↗</span>
          </Link>
          <MessageButton provider={provider} />
        </div>
      </div>
    </article>
  );
}

export default function ServiceProviders() {
  const { data, loading, error, retry } = usePublicData('/users/public-vendors');
  const [params, setParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const query = params.get('q') || '';
  const service = params.get('service') || '';
  const port = params.get('port') || '';
  const country = params.get('country') || '';
  const sort = params.get('sort') || 'newest';

  const providers = useMemo(
    () => Array.isArray(data?.vendors) ? data.vendors : [],
    [data]
  );

  const options = useMemo(() => {
    const unique = field => [
      ...new Set(providers.flatMap(provider => list(provider[field])))
    ].sort((a, b) => a.localeCompare(b));

    return {
      service: unique('serviceCategories'),
      port: unique('ports'),
      country: unique('countries')
    };
  }, [providers]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();

    const result = providers.filter(provider => {
      const services = list(provider.serviceCategories);
      const ports = list(provider.ports);
      const countries = list(provider.countries);

      const searchable = [
        companyName(provider),
        description(provider),
        text(provider.location),
        ...services,
        ...ports,
        ...countries
      ].join(' ').toLowerCase();

      return (
        (!term || searchable.includes(term)) &&
        (!service || services.includes(service)) &&
        (!port || ports.includes(port)) &&
        (!country || countries.includes(country))
      );
    });

    return result.sort((a, b) => {
      if (sort === 'name') {
        return companyName(a).localeCompare(companyName(b));
      }

      if (sort === 'jobs') {
        return count(b.completedJobs) - count(a.completedJobs);
      }

      if (sort === 'rating') {
        const ratingA = count(a.totalRatings) ? count(a.rating) : -1;
        const ratingB = count(b.totalRatings) ? count(b.rating) : -1;

        return ratingB - ratingA ||
          count(b.totalRatings) - count(a.totalRatings);
      }

      return (Date.parse(b.createdAt) || 0) -
        (Date.parse(a.createdAt) || 0);
    });
  }, [providers, query, service, port, country, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const requestedPage = Number(params.get('page') || 1);
  const page = Math.min(
    totalPages,
    Number.isSafeInteger(requestedPage) && requestedPage > 0
      ? requestedPage
      : 1
  );

  const visible = filtered.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(params);

    if (value) next.set(key, value);
    else next.delete(key);

    next.delete('page');
    setParams(next, { replace: true });
  };

  const changePage = value => {
    const next = new URLSearchParams(params);
    next.set('page', String(value));
    setParams(next);

    document.getElementById('spd-results')?.scrollIntoView({
      block: 'start'
    });
  };

  const resetFilters = () => {
    const next = new URLSearchParams();

    if (sort !== 'newest') next.set('sort', sort);

    setParams(next, { replace: true });
  };

  const activeFilters = [query, service, port, country].filter(Boolean).length;

  return (
    <div className="spd-page">
      <FrontHeader />

      <main className="spd-shell">
        <header className="spd-heading">
          <span className="spd-eyebrow">THE MARITIME DIRECTORY</span>
          <h1>Find your next <span>service partner.</span></h1>
          <p>
            Explore maritime companies, compare their services and connect
            with the right team for your vessel.
          </p>
        </header>

        <div className="spd-layout">
          <aside className="spd-sidebar">
            <button
              type="button"
              className="spd-button spd-mobile-toggle"
              aria-expanded={filtersOpen}
              aria-controls="spd-filter-panel"
              onClick={() => setFiltersOpen(value => !value)}
            >
              <i className="fas fa-sliders-h" aria-hidden="true" />
              Filters {activeFilters ? `(${activeFilters})` : ''}
              <span aria-hidden="true">{filtersOpen ? '−' : '+'}</span>
            </button>

            <div
              id="spd-filter-panel"
              className={`spd-panel spd-filters${filtersOpen ? ' is-open' : ''}`}
            >
              <div className="spd-section-heading">
                <h2>Find providers</h2>
                {activeFilters > 0 && (
                  <button
                    type="button"
                    className="spd-text-button"
                    onClick={resetFilters}
                  >
                    Reset
                  </button>
                )}
              </div>

              <label className="spd-field" htmlFor="spd-search">
                <span>Search</span>
                <div className="spd-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    id="spd-search"
                    type="search"
                    placeholder="Company, service or location"
                    value={query}
                    onChange={event => updateFilter('q', event.target.value)}
                  />
                </div>
              </label>

              {[
                ['service', 'Service category', service],
                ['port', 'Port', port],
                ['country', 'Country', country]
              ].map(([key, label, value]) => (
                <label className="spd-field" key={key} htmlFor={`spd-${key}`}>
                  <span>{label}</span>
                  <select
                    id={`spd-${key}`}
                    value={value}
                    onChange={event => updateFilter(key, event.target.value)}
                  >
                    <option value="">All {key === 'country' ? 'countries' : `${key}s`}</option>
                    {value && !options[key].includes(value) && (
                      <option value={value}>{value}</option>
                    )}
                    {options[key].map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
              ))}

              <div className="spd-sidebar-note">
                <i className="fas fa-compass" aria-hidden="true" />
                <p>
                  Open a company profile to see its services, coverage
                  and certifications.
                </p>
              </div>
            </div>
          </aside>

          <section className="spd-results" id="spd-results" aria-label="Providers">
            <div className="spd-toolbar">
              <p aria-live="polite">
                {loading ? 'Loading directory…' : (
                  <><strong>{filtered.length}</strong> providers found</>
                )}
              </p>

              <label className="spd-sort">
                <span>Sort by</span>
                <select
                  value={sort}
                  onChange={event => updateFilter('sort', event.target.value)}
                >
                  <option value="newest">Newest</option>
                  <option value="rating">Highest rated</option>
                  <option value="jobs">Completed jobs</option>
                  <option value="name">Company A–Z</option>
                </select>
              </label>
            </div>

            {loading || error ? (
              <PageState loading={loading} error={error} retry={retry} />
            ) : visible.length ? (
              <>
                <div className="spd-card-list">
                  {visible.map(provider => (
                    <ProviderCard key={provider._id} provider={provider} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav className="spd-pagination" aria-label="Provider pages">
                    <button
                      type="button"
                      className="spd-button"
                      disabled={page === 1}
                      onClick={() => changePage(page - 1)}
                    >
                      Previous
                    </button>
                    <span>Page {page} of {totalPages}</span>
                    <button
                      type="button"
                      className="spd-button"
                      disabled={page === totalPages}
                      onClick={() => changePage(page + 1)}
                    >
                      Next
                    </button>
                  </nav>
                )}
              </>
            ) : (
              <div className="spd-panel spd-state">
                <i className="fas fa-search" aria-hidden="true" />
                <h2>No matching providers</h2>
                <p>
                  {providers.length
                    ? 'Try another keyword or clear your filters.'
                    : 'Public provider profiles will appear here when available.'}
                </p>
                {activeFilters > 0 && (
                  <button
                    type="button"
                    className="spd-button"
                    onClick={resetFilters}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </main>

      <FrontFooter />
    </div>
  );
}